/**
 * След АВТООДОБРЕНИЯ версии (docs/ai-lab.md, `VersionAutoApprovalSchema` контрактов) — в строке
 * бэклога и в шапке черновика. Автоодобрение — обдуманное исключение из FR-4.1 для бренда,
 * который его разрешил, и экран обязан говорить, что решения человека по этой версии НЕ было.
 *
 *   • `issued` — пилюля «автоодобрен»: ключ выпустил бэкенд, человек версию не смотрел;
 *   • `skipped` — пилюля «не автоодобрен» с причиной в подсказке: автоодобрение посмотрело и
 *     отказалось (проверки, картинка, канал, демон подписи, нет слота) и повторять не будет —
 *     решать человеку.
 *
 * ПОЧЕМУ НЕ «АВТО». Слово «авто» уже занято: `AutoBadge` — черновик ЗАКАЗАН расписанием
 * (`BacklogItem.auto`, релиз 2026-10-14). Это другой вопрос — кто пост ПОРОДИЛ, а не кто его
 * ОДОБРИЛ, — и одно слово на два ответа сделало бы самый дорогой факт («человек не одобрял»)
 * неотличимым от безобидного («тему выбрало расписание»). У поста бывают оба признака сразу.
 *
 * Форма — как у `AutoBadge` (контур без заливки, не статус: пилюля статуса рядом остаётся
 * «запланирован» или «ожидает»). Выданная — акцентом, пропуск — серым: первая — факт о ключе,
 * второй — подсказка.
 *
 * Один компонент на бэклог и шапку черновика — подпись и подсказка не могут разойтись.
 */

import type { JSX } from 'react';
import type { VersionAutoApproval } from '../api/types.ts';
import styles from './AutoApprovalBadge.module.css';
import { shortMsk } from './format.ts';

export const AUTO_APPROVED_TEXT = 'автоодобрен';
export const AUTO_SKIPPED_TEXT = 'не автоодобрен';

/** Подсказка пилюли — она же часть имени строки бэклога для чтения с экрана. */
export function autoApprovalTitle(a: VersionAutoApproval): string {
  if (a.state === 'issued') {
    return `одобрен автоматически ${shortMsk(a.at)} — без решения человека`;
  }
  const why = a.reason === null || a.reason.trim() === '' ? 'причина не записана' : a.reason;
  return `автоодобрение пропустило версию ${shortMsk(a.at)}: ${why}. Решение — за человеком`;
}

export function AutoApprovalBadge({
  approval,
}: {
  readonly approval: VersionAutoApproval;
}): JSX.Element {
  const issued = approval.state === 'issued';
  return (
    <span
      className={`pill ${styles['badge']} ${issued ? styles['issued'] : styles['skipped']}`}
      title={autoApprovalTitle(approval)}
      data-testid="auto-approval-badge"
    >
      {issued ? AUTO_APPROVED_TEXT : AUTO_SKIPPED_TEXT}
    </span>
  );
}
